import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  Percent, 
  Calendar, 
  TrendingUp, 
  ArrowLeft, 
  Check, 
  AlertCircle, 
  Sparkles, 
  Calculator,
  Download,
  Clock,
  Info
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { AdUnit } from './AdUnit';

const CURRENCY_MAP = {
  USD: { symbol: '$', label: 'USD ($)', code: 'en-US' },
  EUR: { symbol: '€', label: 'EUR (€)', code: 'de-DE' },
  GBP: { symbol: '£', label: 'GBP (£)', code: 'en-GB' },
  INR: { symbol: '₹', label: 'INR (₹)', code: 'en-IN' },
  LKR: { symbol: 'Rs', label: 'LKR (Rs)', code: 'en-LK' },
  CAD: { symbol: 'C$', label: 'CAD (C$)', code: 'en-CA' },
  AUD: { symbol: 'A$', label: 'AUD (A$)', code: 'en-AU' },
  NZD: { symbol: 'NZ$', label: 'NZD ($)', code: 'en-NZ' },
  SGD: { symbol: 'S$', label: 'SGD ($)', code: 'en-SG' },
  JPY: { symbol: '¥', label: 'JPY (¥)', code: 'ja-JP' },
  CNY: { symbol: '¥', label: 'CNY (¥)', code: 'zh-CN' },
  KRW: { symbol: '₩', label: 'KRW (₩)', code: 'ko-KR' },
  AED: { symbol: 'د.إ', label: 'AED (د.إ)', code: 'ar-AE' },
  SAR: { symbol: 'ر.س', label: 'SAR (ر.س)', code: 'ar-SA' },
  QAR: { symbol: 'ر.ق', label: 'QAR (ر.ق)', code: 'ar-QA' },
  KWD: { symbol: 'د.ك', label: 'KWD (د.ك)', code: 'ar-KW' },
  BHD: { symbol: '.د.ب', label: 'BHD (.د.ب)', code: 'ar-BH' },
  OMR: { symbol: 'ر.ع.', label: 'OMR (ر.ع.)', code: 'ar-OM' },
  CHF: { symbol: 'Fr', label: 'CHF (Fr)', code: 'de-CH' },
  ZAR: { symbol: 'R', label: 'ZAR (R)', code: 'en-ZA' },
  MXN: { symbol: '$', label: 'MXN ($)', code: 'es-MX' },
  BRL: { symbol: 'R$', label: 'BRL (R$)', code: 'pt-BR' },
  RUB: { symbol: '₽', label: 'RUB (₽)', code: 'ru-RU' },
  TRY: { symbol: '₺', label: 'TRY (₺)', code: 'tr-TR' },
  PKR: { symbol: 'Rs', label: 'PKR (Rs)', code: 'en-PK' },
  BDT: { symbol: '৳', label: 'BDT (৳)', code: 'bn-BD' },
  IDR: { symbol: 'Rp', label: 'IDR (Rp)', code: 'id-ID' },
  MYR: { symbol: 'RM', label: 'MYR (RM)', code: 'ms-MY' },
  PHP: { symbol: '₱', label: 'PHP (₱)', code: 'en-PH' },
  THB: { symbol: '฿', label: 'THB (฿)', code: 'th-TH' },
  VND: { symbol: '₫', label: 'VND (₫)', code: 'vi-VN' }
};

export const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(250000);
  const [interestRate, setInterestRate] = useState(5.5); // APR %
  const [loanTerm, setLoanTerm] = useState(30); // in years
  const [termType, setTermType] = useState('years'); // years, months
  const [extraPayment, setExtraPayment] = useState(0);
  const [currency, setCurrency] = useState('USD');

  // Result state parameters
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [amortization, setAmortization] = useState([]);
  const [savingsWithExtra, setSavingsWithExtra] = useState({ interestSaved: 0, monthsSaved: 0 });

  useEffect(() => {
    calculateLoan();
  }, [loanAmount, interestRate, loanTerm, termType, extraPayment]);

  const calculateLoan = () => {
    const principal = parseFloat(loanAmount) || 0;
    const apr = parseFloat(interestRate) || 0;
    const term = parseFloat(loanTerm) || 0;
    const extra = parseFloat(extraPayment) || 0;

    if (principal <= 0 || apr <= 0 || term <= 0) {
      setMonthlyPayment(0);
      setTotalInterest(0);
      setTotalPayment(0);
      setAmortization([]);
      return;
    }

    const monthlyRate = apr / 100 / 12;
    const numberOfPayments = termType === 'years' ? term * 12 : term;

    // Standard formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let baseMonthly = 0;
    if (monthlyRate === 0) {
      baseMonthly = principal / numberOfPayments;
    } else {
      baseMonthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    }

    setMonthlyPayment(baseMonthly);

    // Amortization Schedule build (with and without extra payments)
    let balance = principal;
    const schedule = [];
    let accumulativeInterest = 0;
    let accumulativePrincipal = 0;

    for (let i = 1; i <= numberOfPayments; i++) {
      if (balance <= 0) break;

      const interestPaid = balance * monthlyRate;
      let principalPaid = Math.min(balance, (baseMonthly - interestPaid) + extra);
      balance -= principalPaid;

      accumulativeInterest += interestPaid;
      accumulativePrincipal += principalPaid;

      schedule.push({
        paymentNumber: i,
        interest: interestPaid,
        principal: principalPaid,
        remainingBalance: Math.max(0, balance),
        totalInterestSoFar: accumulativeInterest
      });
    }

    setTotalInterest(accumulativeInterest);
    setTotalPayment(accumulativePrincipal + accumulativeInterest);
    setAmortization(schedule);

    // Calculate how much interest and months were saved by extra payments
    if (extra > 0) {
      let normBalance = principal;
      let normInterest = 0;
      let normMonths = 0;

      for (let i = 1; i <= numberOfPayments; i++) {
        if (normBalance <= 0) break;
        const interestPaid = normBalance * monthlyRate;
        const principalPaid = Math.min(normBalance, baseMonthly - interestPaid);
        normBalance -= principalPaid;
        normInterest += interestPaid;
        normMonths++;
      }

      const savedInterest = Math.max(0, normInterest - accumulativeInterest);
      const savedMonths = Math.max(0, normMonths - schedule.length);

      setSavingsWithExtra({
        interestSaved: savedInterest,
        monthsSaved: savedMonths
      });
    } else {
      setSavingsWithExtra({ interestSaved: 0, monthsSaved: 0 });
    }
  };

  const formatCurrency = (val) => {
    const config = CURRENCY_MAP[currency] || CURRENCY_MAP.USD;
    return new Intl.NumberFormat(config.code, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(val);
  };

  // SVG pie chart properties calculations
  const totalVal = loanAmount + totalInterest;
  const principalPercentage = totalVal > 0 ? (loanAmount / totalVal) * 100 : 50;
  const interestPercentage = totalVal > 0 ? (totalInterest / totalVal) * 100 : 50;

  // SVG Pie sector properties (circumference = 314.16 for r=50)
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (principalPercentage / 100) * circumference;

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Loan & Mortgage Planner</span>
      </div>


      {/* Main Core Form & Analytics Dashboard */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="loan-planner-grid">
        
        {/* Left Column Parameters Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '4px' }}>
              <span style={labelStyle}>Loan Parameters Settings</span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                {Object.keys(CURRENCY_MAP).map((c) => (
                  <option key={c} value={c}>{CURRENCY_MAP[c].label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Principal Amount field */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={fieldLabelStyle}>Principal Loan Amount</label>
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)' }}>
                    {formatCurrency(loanAmount)}
                  </span>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: '12px', fontSize: '1rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                    {CURRENCY_MAP[currency]?.symbol || '$'}
                  </span>
                  <input
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ ...inputStyle, paddingLeft: '32px' }}
                  />
                </div>
                <input
                  type="range"
                  min="5000"
                  max="1500000"
                  step="5000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  style={rangeStyle}
                />
              </div>

              {/* Interest APR field */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={fieldLabelStyle}>Annual Interest Rate (APR %)</label>
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)' }}>
                    {interestRate}%
                  </span>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Percent size={14} style={{ position: 'absolute', left: '12px', color: 'var(--text-muted)' }} />
                  <input
                    type="number"
                    step="0.1"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Math.max(0, parseFloat(e.target.value) || 0))}
                    style={{ ...inputStyle, paddingLeft: '32px' }}
                  />
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="25"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  style={rangeStyle}
                />
              </div>

              {/* Term Duration & type selector */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={fieldLabelStyle}>Loan Repayment Term</label>
                  <div style={{ display: 'flex', background: 'var(--bg-secondary)', borderRadius: '6px', padding: '2px' }}>
                    <button
                      onClick={() => setTermType('years')}
                      style={{
                        border: 'none',
                        background: termType === 'years' ? '#ffffff' : 'transparent',
                        color: termType === 'years' ? 'var(--primary)' : 'var(--text-secondary)',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Years
                    </button>
                    <button
                      onClick={() => setTermType('months')}
                      style={{
                        border: 'none',
                        background: termType === 'months' ? '#ffffff' : 'transparent',
                        color: termType === 'months' ? 'var(--primary)' : 'var(--text-secondary)',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Months
                    </button>
                  </div>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar size={14} style={{ position: 'absolute', left: '12px', color: 'var(--text-muted)' }} />
                  <input
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ ...inputStyle, paddingLeft: '32px' }}
                  />
                </div>
                <input
                  type="range"
                  min="1"
                  max={termType === 'years' ? '50' : '360'}
                  step="1"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(parseInt(e.target.value))}
                  style={rangeStyle}
                />
              </div>

              {/* Optional Extra Monthly Payment field */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px dashed var(--border-color)', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ ...fieldLabelStyle, color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Sparkles size={14} /> Extra Monthly Payment (Principal)
                  </label>
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#059669' }}>
                    +{formatCurrency(extraPayment)}
                  </span>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: '12px', fontSize: '1rem', fontWeight: 700, color: '#059669' }}>
                    {CURRENCY_MAP[currency]?.symbol || '$'}
                  </span>
                  <input
                    type="number"
                    value={extraPayment}
                    onChange={(e) => setExtraPayment(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ ...inputStyle, paddingLeft: '32px', borderColor: 'rgba(5, 150, 105, 0.3)' }}
                  />
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* Right Column Core Analytics metrics results dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          <div style={cardStyle}>
            <span style={labelStyle}>Repayment Calculations Overview</span>

            {/* Monthly Estimate Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              color: '#ffffff',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Estimated Monthly Payment
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#38bdf8', marginBlock: '8px', letterSpacing: '-0.02em' }}>
                {formatCurrency(monthlyPayment + extraPayment)}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Clock size={12} /> Excludes property taxes or insurance policies.
              </div>
            </div>

            {/* Sizing grid statistics lists */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBlock: '8px' }}>
              <div style={statBoxStyle}>
                <span style={statBoxLabel}>TOTAL INTEREST</span>
                <span style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-primary)' }}>{formatCurrency(totalInterest)}</span>
              </div>
              <div style={statBoxStyle}>
                <span style={statBoxLabel}>TOTAL COST (P + I)</span>
                <span style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-primary)' }}>{formatCurrency(totalPayment)}</span>
              </div>
            </div>

            {/* Savings stats from Extra Principal */}
            {extraPayment > 0 && (savingsWithExtra.interestSaved > 0 || savingsWithExtra.monthsSaved > 0) && (
              <div style={{
                background: 'rgba(5, 150, 105, 0.05)',
                border: '1.5px solid rgba(5, 150, 105, 0.15)',
                color: '#059669',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}><Sparkles size={14} /> Extra Repayments Impact:</span>
                <span>You will save **{formatCurrency(savingsWithExtra.interestSaved)}** in lifetime interest payments and pay off your entire loan **{savingsWithExtra.monthsSaved} months** earlier!</span>
              </div>
            )}

            {/* Visual breakdown graphs chart */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
              
              {/* Radial donut SVG */}
              <svg width="80" height="80" viewBox="0 0 120 120">
                <circle 
                  cx="60" 
                  cy="60" 
                  r={radius} 
                  fill="transparent" 
                  stroke="rgba(14, 165, 233, 0.15)" 
                  strokeWidth="14" 
                />
                <circle 
                  cx="60" 
                  cy="60" 
                  r={radius} 
                  fill="transparent" 
                  stroke="var(--primary)" 
                  strokeWidth="14" 
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 60 60)"
                  strokeLinecap="round"
                />
              </svg>

              {/* Dynamic Legend labels */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)' }} /> Principal Amount</span>
                  <span style={{ fontWeight: 800 }}>{Math.round(principalPercentage)}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(14, 165, 233, 0.3)' }} /> Total Interest</span>
                  <span style={{ fontWeight: 800 }}>{Math.round(interestPercentage)}%</span>
                </div>
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* Amortization schedule output block */}
      {amortization.length > 0 && (
        <div style={{
          background: '#ffffff',
          border: '1px solid var(--border-color)',
          borderRadius: '20px',
          padding: '24px',
          marginTop: '40px',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>Amortization Breakdown</h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Repayment schedule index progression month-by-month</p>
            </div>
          </div>

          {/* Table display frame container */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                  <th style={{ padding: '12px 10px', fontWeight: 800 }}>PMT #</th>
                  <th style={{ padding: '12px 10px', fontWeight: 800 }}>PRINCIPAL PAID</th>
                  <th style={{ padding: '12px 10px', fontWeight: 800 }}>INTEREST PAID</th>
                  <th style={{ padding: '12px 10px', fontWeight: 800 }}>INTEREST SO FAR</th>
                  <th style={{ padding: '12px 10px', fontWeight: 800 }}>REMAINING BALANCE</th>
                </tr>
              </thead>
              <tbody>
                {amortization.slice(0, 48).map((row) => (
                  <tr key={row.paymentNumber} style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                    <td style={{ padding: '10px', fontWeight: 700 }}>Month {row.paymentNumber}</td>
                    <td style={{ padding: '10px' }}>{formatCurrency(row.principal)}</td>
                    <td style={{ padding: '10px', color: '#ef4444' }}>{formatCurrency(row.interest)}</td>
                    <td style={{ padding: '10px', color: 'var(--text-secondary)' }}>{formatCurrency(row.totalInterestSoFar)}</td>
                    <td style={{ padding: '10px', fontWeight: 700 }}>{formatCurrency(row.remainingBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {amortization.length > 48 && (
            <div style={{
              textAlign: 'center',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-color)',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              fontWeight: 700
            }}>
              Showing initial 48 amortization months of {amortization.length} total payment installments.
            </div>
          )}

        </div>
      )}

      <style>{`
        @media (min-width: 992px) {
          .loan-planner-grid {
            grid-template-columns: 1fr 440px !important;
          }
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="loan-calculator" />
    </div>
  );
};

// Design system layouts objects
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const fieldLabelStyle = {
  fontSize: '0.8rem',
  fontWeight: 700,
  color: 'var(--text-secondary)'
};

const inputStyle = {
  width: '100%',
  height: '42px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  outline: 'none',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  background: 'var(--bg-secondary)',
  paddingLeft: '14px',
  boxSizing: 'border-box'
};

const rangeStyle = {
  width: '100%',
  accentColor: 'var(--primary)',
  cursor: 'pointer',
  marginTop: '4px'
};

const statBoxStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
};

const statBoxLabel = {
  fontSize: '0.625rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  letterSpacing: '0.04em'
};

export default LoanCalculator;
